/**
 * Date and Time utilities with timezone support
 * Backend stores all dates in UTC
 * Frontend displays dates in user's selected timezone
 */

import { format, formatDistance, formatRelative, parseISO } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { enUS, es } from 'date-fns/locale';

// Supported locales
const locales = {
  en: enUS,
  es: es,
};

/**
 * Get date-fns locale from language code
 */
export const getLocale = (languageCode: string) => {
  return locales[languageCode as keyof typeof locales] || locales.en;
};

/**
 * Format a bare wall-clock time string ("HH:mm" or "HH:mm:ss") as a 12-hour
 * time with an AM/PM suffix ("9:00 AM", "1:30 PM").
 *
 * These values (e.g. weekly-schedule availability slots) are timezone-agnostic
 * wall-clock strings, NOT datetimes, so they must NOT be passed through the
 * timezone-converting formatters (formatDate/formatDateInTimezone) — doing so
 * would shift them. Returns the input unchanged if it is not a parseable
 * HH:mm[:ss] string. (TRUS-259)
 */
export const formatTimeString12h = (time: string | null | undefined): string => {
  if (!time) return '';
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(time.trim());
  if (!m) return time;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (hours > 23 || minutes > 59) return time;
  const period = hours < 12 ? 'AM' : 'PM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${String(minutes).padStart(2, '0')} ${period}`;
};

/**
 * Normalize a possibly-naive UTC ISO string to one JS will parse AS UTC.
 * The backend stores UTC but omits the 'Z' suffix (e.g. "2026-07-20T18:57:00"),
 * which `new Date()` would otherwise treat as LOCAL time (showing the UTC
 * wall-clock instead of the viewer's real local time).
 */
const normalizeUtc = (s: string): string =>
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) &&
  !s.endsWith('Z') &&
  !s.includes('+') &&
  !/[-+]\d{2}:\d{2}$/.test(s)
    ? s + 'Z'
    : s;

/**
 * Format a (UTC) timestamp as date + time in the browser's SYSTEM timezone.
 * Use for user-facing timestamps previously rendered via a bare
 * `new Date(x).toLocaleString()` (which mis-parsed naive UTC strings).
 */
export const formatLocalDateTime = (
  date?: string | null,
  opts?: Intl.DateTimeFormatOptions,
): string => {
  if (!date) return '';
  try {
    return new Date(normalizeUtc(date)).toLocaleString(undefined, opts);
  } catch {
    return String(date);
  }
};

/** Format a (UTC) timestamp as a date only, in the browser's SYSTEM timezone. */
export const formatLocalDate = (
  date?: string | null,
  opts?: Intl.DateTimeFormatOptions,
): string => {
  if (!date) return '';
  try {
    return new Date(normalizeUtc(date)).toLocaleDateString(undefined, opts);
  } catch {
    return String(date);
  }
};

/**
 * Calculate age in whole years from an ISO date-only string (YYYY-MM-DD).
 * Uses the current local date so the result updates each time it is called
 * (no stale snapshot). Returns null for empty/invalid input.
 */
export function computeAge(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age;
}

/**
 * Format date in user's timezone
 * @param date - ISO date string or Date object (assumed to be UTC)
 * @param timezone - IANA timezone identifier (e.g., 'America/Los_Angeles')
 * @param formatString - date-fns format string (default: 'PPpp')
 * @param languageCode - Language code for localization (default: 'en')
 */
export const formatDateInTimezone = (
  date: string | Date,
  timezone: string = 'America/Los_Angeles',
  formatString: string = 'PPpp',
  languageCode: string = 'en'
): string => {
  try {
    // Backend stores all dates in UTC but may omit the Z suffix.
    // Append Z to bare ISO strings so parseISO treats them as UTC.
    let normalized = date;
    if (typeof normalized === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(normalized) && !normalized.endsWith('Z') && !normalized.includes('+') && !/[-+]\d{2}:\d{2}$/.test(normalized)) {
      normalized = normalized + 'Z';
    }
    const dateObj = typeof normalized === 'string' ? parseISO(normalized) : normalized;
    const locale = getLocale(languageCode);

    return formatInTimeZone(dateObj, timezone, formatString, { locale });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Format date using common formats
 */
export const formatDate = {
  /**
   * Short date (e.g., "12/31/2023")
   */
  short: (date: string | Date, timezone: string, languageCode: string = 'en') =>
    formatDateInTimezone(date, timezone, 'P', languageCode),

  /**
   * Medium date (e.g., "Dec 31, 2023")
   */
  medium: (date: string | Date, timezone: string, languageCode: string = 'en') =>
    formatDateInTimezone(date, timezone, 'PP', languageCode),

  /**
   * Long date (e.g., "December 31, 2023")
   */
  long: (date: string | Date, timezone: string, languageCode: string = 'en') =>
    formatDateInTimezone(date, timezone, 'PPP', languageCode),

  /**
   * Full date (e.g., "Sunday, December 31, 2023")
   */
  full: (date: string | Date, timezone: string, languageCode: string = 'en') =>
    formatDateInTimezone(date, timezone, 'PPPP', languageCode),

  /**
   * Short time (e.g., "2:30 PM")
   */
  time: (date: string | Date, timezone: string, languageCode: string = 'en') =>
    formatDateInTimezone(date, timezone, 'p', languageCode),

  /**
   * Medium time (e.g., "2:30:45 PM")
   */
  timeWithSeconds: (date: string | Date, timezone: string, languageCode: string = 'en') =>
    formatDateInTimezone(date, timezone, 'pp', languageCode),

  /**
   * Short date and time (e.g., "12/31/2023, 2:30 PM")
   */
  dateTime: (date: string | Date, timezone: string, languageCode: string = 'en') =>
    formatDateInTimezone(date, timezone, 'Pp', languageCode),

  /**
   * Full date and time (e.g., "Sunday, December 31, 2023 at 2:30 PM")
   */
  dateTimeFull: (date: string | Date, timezone: string, languageCode: string = 'en') =>
    formatDateInTimezone(date, timezone, 'PPPPp', languageCode),
};

/**
 * Format relative time (e.g., "2 hours ago", "in 3 days")
 */
export const formatRelativeTime = (
  date: string | Date,
  timezone: string,
  languageCode: string = 'en'
): string => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    const zonedDate = toZonedTime(dateObj, timezone);
    const now = new Date();
    const locale = getLocale(languageCode);

    return formatDistance(zonedDate, now, { addSuffix: true, locale });
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return '';
  }
};

/**
 * Format relative date (e.g., "last Friday at 2:30 PM", "tomorrow at 10:00 AM")
 */
export const formatRelativeDate = (
  date: string | Date,
  timezone: string,
  languageCode: string = 'en'
): string => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    const zonedDate = toZonedTime(dateObj, timezone);
    const now = new Date();
    const locale = getLocale(languageCode);

    return formatRelative(zonedDate, now, { locale });
  } catch (error) {
    console.error('Error formatting relative date:', error);
    return '';
  }
};

/**
 * Convert local datetime to UTC for backend storage
 * @param date - Date object in user's local timezone
 * @param timezone - IANA timezone identifier
 */
export const toUTC = (date: Date, timezone: string): Date => {
  try {
    // When sending to backend, we need to ensure the date is in UTC
    return toZonedTime(date, 'UTC');
  } catch (error) {
    console.error('Error converting to UTC:', error);
    return date;
  }
};

/**
 * Common timezone list for selector
 */
export const COMMON_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)', offset: 'UTC-5/-4' },
  { value: 'America/Chicago', label: 'Central Time (CT)', offset: 'UTC-6/-5' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', offset: 'UTC-7/-6' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)', offset: 'UTC-7' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', offset: 'UTC-8/-7' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)', offset: 'UTC-9/-8' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)', offset: 'UTC-10' },
  { value: 'America/Puerto_Rico', label: 'Atlantic Time (Puerto Rico)', offset: 'UTC-4' },
];

/**
 * Format a date-only string (YYYY-MM-DD) as local date WITHOUT timezone conversion.
 * Use this for dates like DOB where we want to display exactly the date that was entered,
 * without any timezone shifting.
 *
 * @param dateStr - Date string in YYYY-MM-DD format (or any format with year-month-day)
 * @param locale - Locale for formatting (default: 'en-US')
 * @returns Formatted date string or '-' if invalid
 *
 * @example
 * formatDateOnly('1976-01-01') // Returns "1/1/1976" (not "12/31/1975" due to timezone)
 */
export const formatDateOnly = (
  dateStr: string | null | undefined,
  locale: string = 'en-US'
): string => {
  if (!dateStr) return '-';

  try {
    // Parse date string as local date, not UTC
    // Split by common delimiters and construct local date
    const parts = dateStr.split(/[-/T]/);
    if (parts.length >= 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // months are 0-indexed
      const day = parseInt(parts[2], 10);

      // Create date as local (not UTC)
      const date = new Date(year, month, day);

      if (isNaN(date.getTime())) {
        return '-';
      }

      return date.toLocaleDateString(locale);
    }
    return '-';
  } catch {
    return '-';
  }
};

/**
 * Today's date as YYYY-MM-DD in the user's local timezone (not UTC).
 * Use for "is this date in the future?" guards on date-only inputs where
 * `new Date().toISOString().split('T')[0]` is wrong (UTC may already have
 * rolled over while the user is still on the previous local day).
 */
export const todayLocalISO = (): string => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Epoch-millisecond timestamps below this threshold are ambiguous with plain
 * integers, so only numbers at/above it are treated as epoch. ~1973-03-03 in
 * ms — small enough to cover any real record, large enough to never collide
 * with a compact numeric date like 20240115 (an 8-digit YYYYMMDD).
 */
const EPOCH_MS_FLOOR = 1e11;

/**
 * Parse a value that might be an epoch-millisecond timestamp (number or numeric
 * string) into a Date, or null when it isn't one. Legacy records imported from
 * Trust store date/datetime field values as epoch-ms strings (e.g.
 * "1749153087367"), which native date inputs cannot consume.
 */
const parseEpochMs = (s: string): Date | null => {
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n < EPOCH_MS_FLOOR) return null;
  const d = new Date(n);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Normalize an arbitrary date value to the `YYYY-MM-DD` string that a native
 * `<input type="date">` requires. A native date input silently rejects any
 * other format (e.g. a localized display string like "6/24/2026" or an
 * epoch-ms timestamp), which makes a controlled input un-editable and hides an
 * existing value. Handles already-ISO values, ISO datetimes, US display
 * strings ("M/D/YYYY"), and epoch-ms timestamps. Returns '' if it can't parse.
 */
export const toDateInputValue = (value: any): string => {
  if (value === null || value === undefined || value === '') return '';
  const s = String(value).trim();
  // Already ISO (date or datetime) -> take the date part.
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  // US display "M/D/YYYY" (or with leading zeros).
  const usMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (usMatch) {
    const month = usMatch[1].padStart(2, '0');
    const day = usMatch[2].padStart(2, '0');
    return `${usMatch[3]}-${month}-${day}`;
  }
  // Epoch-ms timestamp (legacy Trust import) -> local calendar date.
  const epoch = parseEpochMs(s);
  if (epoch) {
    const y = epoch.getFullYear();
    const m = String(epoch.getMonth() + 1).padStart(2, '0');
    const day = String(epoch.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  return '';
};

/**
 * Normalize an arbitrary datetime value to the `YYYY-MM-DDTHH:mm` string that a
 * native `<input type="datetime-local">` requires. Like {@link toDateInputValue}
 * but keeps the local time-of-day. Handles ISO datetimes, plain ISO dates
 * (time defaults to 00:00), and epoch-ms timestamps. Returns '' if it can't parse.
 */
export const toDateTimeLocalInputValue = (value: any): string => {
  if (value === null || value === undefined || value === '') return '';
  const s = String(value).trim();
  // ISO datetime "YYYY-MM-DDTHH:mm[...]" -> keep date + HH:mm.
  const isoDateTime = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (isoDateTime) {
    const [, y, mo, d, h, mi] = isoDateTime;
    return `${y}-${mo}-${d}T${h}:${mi}`;
  }
  // Plain ISO date -> midnight.
  const isoDate = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDate) return `${isoDate[1]}-${isoDate[2]}-${isoDate[3]}T00:00`;
  // Epoch-ms timestamp (legacy Trust import) -> local date + time.
  const epoch = parseEpochMs(s);
  if (epoch) {
    const y = epoch.getFullYear();
    const mo = String(epoch.getMonth() + 1).padStart(2, '0');
    const d = String(epoch.getDate()).padStart(2, '0');
    const h = String(epoch.getHours()).padStart(2, '0');
    const mi = String(epoch.getMinutes()).padStart(2, '0');
    return `${y}-${mo}-${d}T${h}:${mi}`;
  }
  return '';
};

/**
 * All world timezones (for advanced selector)
 */
export const ALL_TIMEZONES = [
  // Americas
  { value: 'America/New_York', label: 'New York (Eastern Time)', region: 'Americas' },
  { value: 'America/Chicago', label: 'Chicago (Central Time)', region: 'Americas' },
  { value: 'America/Denver', label: 'Denver (Mountain Time)', region: 'Americas' },
  { value: 'America/Phoenix', label: 'Phoenix (Arizona)', region: 'Americas' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (Pacific Time)', region: 'Americas' },
  { value: 'America/Anchorage', label: 'Anchorage (Alaska)', region: 'Americas' },
  { value: 'Pacific/Honolulu', label: 'Honolulu (Hawaii)', region: 'Americas' },
  { value: 'America/Mexico_City', label: 'Mexico City', region: 'Americas' },
  { value: 'America/Toronto', label: 'Toronto', region: 'Americas' },
  { value: 'America/Sao_Paulo', label: 'São Paulo', region: 'Americas' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires', region: 'Americas' },

  // Europe
  { value: 'Europe/London', label: 'London (GMT/BST)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Rome', label: 'Rome (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)', region: 'Europe' },

  // Asia
  { value: 'Asia/Dubai', label: 'Dubai (GST)', region: 'Asia' },
  { value: 'Asia/Kolkata', label: 'India (IST)', region: 'Asia' },
  { value: 'Asia/Bangkok', label: 'Bangkok (ICT)', region: 'Asia' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)', region: 'Asia' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', region: 'Asia' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)', region: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)', region: 'Asia' },

  // Pacific
  { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)', region: 'Pacific' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEDT/AEST)', region: 'Pacific' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZDT/NZST)', region: 'Pacific' },

  // Africa
  { value: 'Africa/Cairo', label: 'Cairo (EET)', region: 'Africa' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)', region: 'Africa' },
  { value: 'Africa/Lagos', label: 'Lagos (WAT)', region: 'Africa' },
];
